import React, { useEffect, useMemo } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useEventInfo } from '@shared/utils/eventInfo';
import { addError } from '@shared/utils/logger';
import { EventVenueState } from './useEventVenueDialog';
import { useEventVenueDialogTelemetry } from './EventVenueDialog.telemetry';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { CloseButton, DialogV2, DrawerV2, Flex } from '@withjoy/joykit';
import { JoyInteractableMapProvider } from '@shared/components/JoyInteractableMap/JoyInteractableMap.provider';
import { pxToRem } from '@withjoy/joykit/theme';
import { Map } from '../Map';
import { closeButtonOverrides, drawerOverrides } from '@shared/components/ResponsiveDialog/ResponsiveDialog.styles';
import { StateIndicators } from '../StateIndicators';
import { Intro } from '../Intro';
import { Form } from '../Form';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { Filters } from '../Filters';
import { MapToast } from '../Map/MapToast';
import { Hotels, HotelsExploreCta } from '../Hotels';
import { VirtualScrollableArea } from '@website-editor/utils/WebsiteEditor.utils';
import { VenueError } from '../VenueError';
import { NoHotels } from '../NoHotels';
import { useEventVenueDialogReducer } from '../../hooks/useEventVenueDialogReducer';
import { useVenueDialogTransitions } from '../../hooks/useVenueDialogTransitions';
import { useBestAnchorLocationEffect } from '../../hooks/useBestAnchorLocationEffect';
import { useVenueRevealHotelMatches } from '../../hooks/useVenueRevealHotelMatches';
import { PICKUP_BEST_HOTELS, SHOW_HOTELS_ON_MAP } from '../VenueInfo/VenueInfo.constants';
import { AccommodationHotelsDocument, PageContainerBySlugDocument, ScoringPriorityField } from '@graphql/generated';
import { PlanningRoutes } from '@apps/admin/routes/Planning/Planning.routes';
import { useHistory, useRouterHelper, useTranslation } from '@shared/core';
import { dialogOverrides } from './EventVenueDialog.styles';
import { MobileTransparentDrawer } from '@shared/components/MobileTransparentDrawer';
import { Map as MapIcon } from '@withjoy/joykit/icons';
import { useEventPhoto } from '@shared/utils/eventPhoto';
import { AdvancedLocationInputV2FormFields } from '@shared/components/LocationInputV2';
import { useHotelSearch } from '../../hooks/useHotelSearch';
import { useAutoPopulation } from '@shared/utils/accommodations';
import { useIsAccommodationsTourEnabled } from '@shared/utils/accommodations/useIsAccommodationsTourEnabled';
import { useApolloClient } from '@apollo/client';
import { useUpdateWebsitePreview } from '@apps/admin/routes/WebsiteEditor/hooks/useUpdateWebsitePreview';
import { useAccommodationsContext } from '../../context';
import { useUserConsent } from '@shared/utils/userConsent';

export const searchRadiiKm = [5, 10, 20, 50] as const;

export interface EventVenueDialogProps
  extends Readonly<{
    eventVenueState: EventVenueState;
    isOpen: boolean;
    onClose: () => void;
    pageSlug?: string;
    isSuperAdmin?: boolean;
    onSaveVenue: () => void;
  }> {}

const EventVenueDialogRoot = (props: EventVenueDialogProps) => {
  const { eventVenueState: eventVenueStateArg, isOpen, onClose, pageSlug, isSuperAdmin, onSaveVenue } = props;

  const client = useApolloClient();
  const history = useHistory();
  const { buildPath } = useRouterHelper();
  const telemetry = useEventVenueDialogTelemetry();
  const { eventInfo, eventHandle } = useEventInfo();
  const isMobile = useIsMobileScreen();
  const { accommodationsData } = useAccommodationsContext();
  const { updateWebsitePreview } = useUpdateWebsitePreview();

  const { t } = useTranslation('websiteEditor');
  const venueDialogTrans = t('venueDialog');
  const scrollTopMapText = venueDialogTrans.map();

  const [state, dispatch] = useEventVenueDialogReducer({ eventVenueState: eventVenueStateArg });
  const { goToCreate, goToUpdate, goToIntro, goToFilters, goToHotels, goToError, goToNoHotels } = useVenueDialogTransitions(dispatch);
  const { eventVenueState, newVenueInfo, bestAnchorLocation, scoringPriorityFields, destination, hotels: hotelsState } = state;
  const { data: hotelsWithAccommodationPlaces, allHotelCount, goodMatchedHotelsCount, bestHotelsCount, isLoading: searchingHotels, hasError } = hotelsState;

  const { venueInfo } = useEventPlaces();
  const { eventPhoto } = useEventPhoto(eventHandle, !eventHandle);

  const venue = newVenueInfo?.googlePlaceId ? newVenueInfo : venueInfo;

  useBestAnchorLocationEffect({ venue, eventInfo, eventHandle, dispatch });

  const hasFirstState = [EventVenueState.create, EventVenueState.confirm, EventVenueState.update, EventVenueState.error].includes(eventVenueState);

  useHotelSearch({
    shouldSearch: state.shouldSearchForProperties && !hasFirstState,
    destination,
    scoringPriorityFields,
    searchRadiiKm,
    dispatch,
  });

  const { getUserConsent, setUserConsent } = useUserConsent();
  const { isAccommodationsTourEnabled } = useIsAccommodationsTourEnabled();

  // To handle auto populate accommodations places
  const { callAutoPopulationApiV4, onComplete, status, autoPopulationSummary } = useAutoPopulation({
    mode: 'start-only',
    context: 'EventVenueDialog',
  });

  const onCompleteAutoPopulation = useEventCallback((autoPopulatedAccommodationPlaceIds: ReadonlyArray<string>) => {
    const alreadyExistsCount = accommodationsData?.accommodationPlacesV2?.filter(place => !autoPopulatedAccommodationPlaceIds.includes(place?.id ?? ''))?.length ?? 0;
    autoPopulationSummary.set({ newlyPopulatedCount: autoPopulatedAccommodationPlaceIds.length, alreadyExistsCount });
    onComplete();

    client
      .refetchQueries({
        include: [PageContainerBySlugDocument, AccommodationHotelsDocument],
      })
      .then(() => updateWebsitePreview());
  });

  const isLoading = searchingHotels || status === 'PENDING';

  // Show direction in map only for intro screen.
  const hideDirection = eventVenueState !== EventVenueState.intro;

  const handleOnSaveVenue = useEventCallback(() => {
    // If lighter weight tour is enabled, then use async hotel autopopulation. Otherwise, use existing v4 auto populate and follow the intro flow.
    if (isAccommodationsTourEnabled) {
      // If user has seen the tour, we need to reset it to false, so that the tour can be shown again, after add venue and auto populate hotels.
      if (getUserConsent('hasSeenAccommodationsTour')) {
        telemetry.resetHasSeenAccommodationsTour();
        setUserConsent('hasSeenAccommodationsTour', false);
      }

      onSaveVenue();
      goToUpdate();
      onClose();
    } else {
      goToIntro();
    }
  });

  const handleOnChangeOrder = useEventCallback((scoringPriorityFields: ReadonlyArray<ScoringPriorityField>) => {
    dispatch({ type: 'SET_SCORING_FIELDS', scoringPriorityFields: [...scoringPriorityFields] });
    dispatch({ type: 'TRIGGER_PROPERTY_SEARCH' });
  });

  const failedToSaveVenue = useEventCallback(() => {
    goToError();
    addError('Error add event venue', { context: 'EventVenueDialog', eventHandle });
  });

  const handleOnChangeVenue = useEventCallback((location?: AdvancedLocationInputV2FormFields) => {
    if (location && location.locationValue) {
      const venue = {
        googlePlaceId: location.placeId,
        displayName: location.locationValue,
        name: location.locationValue,
        latitude: location.latitude,
        longitude: location.longitude,
        address: location.address,
      };
      dispatch({ type: 'SET_NEW_VENUE_INFO', venue });
    } else {
      dispatch({ type: 'CLEAR_NEW_VENUE_INFO' });
    }
  });

  useEffect(() => {
    dispatch({ type: 'COMPUTE_AND_SET_DESTINATION', payload: { venue, locationInfo: eventInfo?.locationInfo, bestAnchorLocation } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventInfo?.locationInfo, venue, bestAnchorLocation]);

  useEffect(() => {
    if (isOpen) telemetry.eventVenueDialog.enter(eventVenueState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (eventVenueStateArg) {
      dispatch({ type: 'SET_EVENT_VENUE_STATE', eventVenueState: eventVenueStateArg });
    }
  }, [dispatch, eventVenueStateArg]);

  useEffect(() => {
    const isFromIntroFlow = [EventVenueState.intro, EventVenueState.filters, EventVenueState.hotels].includes(eventVenueState);
    const shouldShowError = hasError && isFromIntroFlow;
    if (shouldShowError) {
      goToNoHotels();
    }
  }, [eventVenueState, goToNoHotels, hasError]);

  const handleOnClickRevealMatches = useVenueRevealHotelMatches({
    eventInfo,
    eventHandle,
    destination,
    eventVenueState,
    scoringPriorityFields,
    searchRadiiKm,
    dispatch,
    goToHotels,
    goToNoHotels,
    callAutoPopulateAccommodationPlacesV4: callAutoPopulationApiV4,
    onCompleteAutoPopulation,
  });

  const onClickWhereToStay = useEventCallback(() => {
    telemetry.hotelsNextCtaClick('PreviewPage', eventVenueState, eventHandle);
    goToUpdate();
    onClose();
  });

  const onClickExploreAllOptions = useEventCallback(() => {
    telemetry.hotelsNextCtaClick('BrowseHotels', eventVenueState, eventHandle);
    onClose();
    history.push(buildPath(`${PlanningRoutes.accommodations}/browse`));
    // @todo - After we update the new Map for admins, we can reconnect this with the new Map.
    // onShowAccommodationConsole();
  });

  const activeIndex = eventVenueState === EventVenueState.intro ? 1 : eventVenueState === EventVenueState.filters ? 2 : 0;

  const renderActiveState = useMemo(() => {
    switch (eventVenueState) {
      case EventVenueState.error:
        return <VenueError />;
      case EventVenueState.noHotels:
        return <NoHotels onClickExploreAllOptions={onClickExploreAllOptions} />;
      case EventVenueState.intro:
        return <Intro onClickContinue={goToFilters} />;
      case EventVenueState.filters:
        return (
          <Filters
            handleOnClickRevealMatches={handleOnClickRevealMatches}
            handleOnChangeOrder={handleOnChangeOrder}
          />
        );
      case EventVenueState.hotels:
        return (
          <Hotels
            isLoading={isLoading}
            hotels={hotelsWithAccommodationPlaces.slice(0, PICKUP_BEST_HOTELS)}
            allHotelCount={allHotelCount}
            bestHotelsCount={bestHotelsCount}
            goodMatchedHotelsCount={goodMatchedHotelsCount}
          >
            {!isLoading && (
              <HotelsExploreCta
                onClickExploreAllOptions={onClickExploreAllOptions}
                onClickWhereToStay={onClickWhereToStay}
              />
            )}
          </Hotels>
        );
      default:
        return (
          <Form
            eventVenueState={eventVenueState}
            venueInfo={venueInfo}
            onSaveVenue={handleOnSaveVenue}
            onError={failedToSaveVenue}
            eventHandle={eventHandle}
            pageSlug={pageSlug}
            isSuperAdmin={isSuperAdmin}
            handleDialogDismiss={() => {
              goToCreate();
              onClose();
            }}
            onChangeVenue={handleOnChangeVenue}
          />
        );
    }
  }, [
    eventVenueState,
    onClickExploreAllOptions,
    goToFilters,
    handleOnClickRevealMatches,
    handleOnChangeOrder,
    isLoading,
    hotelsWithAccommodationPlaces,
    allHotelCount,
    bestHotelsCount,
    goodMatchedHotelsCount,
    onClickWhereToStay,
    venueInfo,
    handleOnSaveVenue,
    failedToSaveVenue,
    eventHandle,
    pageSlug,
    isSuperAdmin,
    handleOnChangeVenue,
    goToCreate,
    onClose,
  ]);

  const renderContent = useMemo(() => {
    return (
      <>
        {renderActiveState}
        {!isAccommodationsTourEnabled && (hasFirstState || [EventVenueState.intro, EventVenueState.filters].includes(eventVenueState)) && (
          <StateIndicators
            numberOfDots={3}
            activeIndex={activeIndex}
          />
        )}
      </>
    );
  }, [activeIndex, eventVenueState, hasFirstState, renderActiveState, isAccommodationsTourEnabled]);

  const renderMap = useMemo(() => {
    return (
      <Flex
        flex={{ sm2: 1 }}
        height={{ _: '100%', sm2: 'auto' }}
        width={'100%'}
      >
        <Map
          isLoading={isLoading}
          hideDirection={hideDirection}
          destination={destination}
          eventVenueState={eventVenueState}
          originList={hotelsWithAccommodationPlaces.slice(0, eventVenueState === EventVenueState.hotels ? PICKUP_BEST_HOTELS : SHOW_HOTELS_ON_MAP)}
          eventPhoto={eventPhoto?.url}
        >
          <MapToast
            isLoading={isLoading}
            eventVenueState={eventVenueState}
            allHotelCount={allHotelCount}
            bestHotelsCount={bestHotelsCount}
          />
        </Map>
      </Flex>
    );
  }, [allHotelCount, bestHotelsCount, destination, eventVenueState, hideDirection, hotelsWithAccommodationPlaces, isLoading, eventPhoto?.url]);

  return (
    <Flex
      width="100%"
      height="100%"
    >
      <CloseButton
        overrides={{
          Root: {
            props: {
              ...closeButtonOverrides.Root?.props,
              position: 'absolute',
              top: eventVenueState === EventVenueState.intro ? pxToRem(20) : pxToRem(12),
              right: eventVenueState === EventVenueState.intro ? 6 : pxToRem(12),
              zIndex: 'popover',
              width: pxToRem(40),
              height: pxToRem(40),
              minWidth: pxToRem(40),
              background: 'white',
              border: '1px solid',
              borderColor: 'mono5',
            },
          },
          Icon: { props: { ...closeButtonOverrides.Icon?.props } },
        }}
        onClick={onClose}
        aria-label={'close dialog'}
        id={'dialog-close'}
      />
      {isMobile ? (
        <>
          {/* Mobile transparent drawer is not working in mobile as expected, so we are not using it for accommodations tour. */}
          {isAccommodationsTourEnabled ? (
            <>{renderContent}</>
          ) : (
            <>
              {renderMap}
              <MobileTransparentDrawer
                scrollTopCta={{ icon: MapIcon, text: scrollTopMapText, closeOnClick: false }}
                isOpenByDefault={true}
                animationType="spring"
              >
                <Flex
                  width={'100%'}
                  overflowY="auto"
                  display={'flex'}
                  flexDirection="column"
                  justifyContent={'space-between'}
                  alignItems={'center'}
                  gap={{ __: 5, sm2: 7 }}
                  paddingY={{ __: 7, sm2: 8 }}
                >
                  {renderContent}
                </Flex>
              </MobileTransparentDrawer>
            </>
          )}
        </>
      ) : (
        <Flex
          width="100%"
          height={{ _: 'auto', sm2: 'auto' }}
          flexDirection={{ _: 'column-reverse', sm2: 'row' }}
        >
          <Flex
            position={'relative'}
            justifyContent={'center'}
            alignItems={'center'}
            flex={eventVenueState === EventVenueState.hotels ? 2 : 1}
            paddingY={{ _: 0, sm2: 0 }}
          >
            <VirtualScrollableArea
              width={'100%'}
              overflowY="auto"
              display={'flex'}
              height={{ __: 'auto', sm2: '83vh' }}
              flexDirection="column"
              justifyContent={'space-between'}
              alignItems={'center'}
              gap={7}
              paddingY={{ __: 5, sm2: 8 }}
            >
              {renderContent}
            </VirtualScrollableArea>
          </Flex>
          {renderMap}
        </Flex>
      )}
    </Flex>
  );
};

export const EventVenueDialog = (props: EventVenueDialogProps) => {
  const { isOpen, onClose, eventVenueState } = props;

  const { eventHandle } = useEventInfo();
  const isMobile = useIsMobileScreen();
  const telemetry = useEventVenueDialogTelemetry();
  const { isAccommodationsTourEnabled } = useIsAccommodationsTourEnabled();

  const handleOnClose = useEventCallback(() => {
    telemetry.closeEventVenueDialog(eventVenueState, eventHandle);
    onClose();
  });

  if (isMobile) {
    return (
      <DrawerV2
        isOpen={isOpen}
        overrides={isAccommodationsTourEnabled ? drawerOverrides : { Content: { props: { width: '100%', height: '100%', overflow: 'hidden' } } }}
        onClose={handleOnClose}
        scrollBehavior="inside"
        disableFocusLockRemoveScroll
        disableAutoFocus
        anchor="bottom"
      >
        <JoyInteractableMapProvider>
          <EventVenueDialogRoot
            {...props}
            onClose={handleOnClose}
          />
        </JoyInteractableMapProvider>
      </DrawerV2>
    );
  }
  return (
    <>
      <DialogV2
        isOpen={isOpen}
        onClose={handleOnClose}
        scrollBehavior="inside"
        size={'5xl'}
        disableAutoFocus
        disableFocusLockRemoveScroll
        overrides={{
          ...dialogOverrides,
          Content: {
            props: {
              height: 'auto',
              overflow: 'hidden',
            },
          },
          ContentContainer: {
            props: {
              width: 'auto',
            },
          },
          Backdrop: { props: { backgroundColor: 'transparent', background: 'rgba(0, 0, 0, 0.60)', backdropFilter: 'blur(25px)' } },
          Body: { props: { ...dialogOverrides, paddingX: 0, paddingY: 0, borderRadius: pxToRem(12), overflowY: 'auto', maxHeight: '90vh' } },
        }}
      >
        <JoyInteractableMapProvider>
          <EventVenueDialogRoot
            {...props}
            onClose={handleOnClose}
          />
        </JoyInteractableMapProvider>
      </DialogV2>
    </>
  );
};
